import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { questionsAPI } from '../services/apiService';
import { useSEO } from '../hooks/useSEO';
import { initializeTracking, trackPasswordVerification } from '../utils/trackingUtils';

const TestEQ = () => {
  // Use SEO hook for EQ Test page
  const { seoData } = useSEO('eq', {
    h1: 'Test EQ - Kiểm tra trí tuệ cảm xúc',
    metaDescription: 'Đánh giá trí tuệ cảm xúc EQ qua bài test với 13 câu hỏi. Hiểu rõ khả năng quản lý cảm xúc và giao tiếp của bạn.',
    title: 'Test EQ Online - Kiểm tra trí tuệ cảm xúc'
  });
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState('intro'); // intro, test, completed, result
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(null); // No time limit for EQ test
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [eqScore, setEqScore] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [reviewLaterQuestions, setReviewLaterQuestions] = useState([]);
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false);
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showQuestionBoard, setShowQuestionBoard] = useState(false);
  const [showNeedAnswerNotification, setShowNeedAnswerNotification] = useState(false);
  const [isReviewingMode, setIsReviewingMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load mật khẩu từ localStorage hoặc sử dụng mặc định
  const [correctPassword, setCorrectPassword] = useState('eq2025');
  
  // Load password from localStorage
  useEffect(() => {
    const savedTestPasswords = localStorage.getItem('test_passwords');
    if (savedTestPasswords) {
      const passwords = JSON.parse(savedTestPasswords);
      setCorrectPassword(passwords.eq || 'eq2025');
    }
  }, []);

  // Load questions from API database
  useEffect(() => {
    const loadQuestions = async () => {
      try {
        setIsLoading(true);
        console.log('🔍 Loading EQ questions from API...');
        
        const apiQuestions = await questionsAPI.getByType('eq');
        console.log(`✅ Loaded ${apiQuestions.length} EQ questions from database`);
        
        if (apiQuestions && apiQuestions.length > 0) {
          setQuestions(apiQuestions);
        } else {
          console.warn('❌ No EQ questions found in database, loading defaults...');
          // Fallback to default hardcoded questions
          setQuestions(getDefaultQuestions());
        }
      } catch (error) {
        console.error('❌ Error loading EQ questions:', error);
        // Fallback to default questions
        setQuestions(getDefaultQuestions());
        console.log(`⚠️ Using fallback: ${getDefaultQuestions().length} default questions`);
      } finally {
        setIsLoading(false);
      }
    };

    loadQuestions();
  }, []);

  // Initialize tracking when component mounts
  useEffect(() => {
    initializeTracking('eq');
  }, []);

  // Default questions fallback function
  const getDefaultQuestions = () => {
    return [
      {
        id: 1,
        statement: "Tôi có thể dễ dàng nhận biết cảm xúc của mình",
        type: "self-awareness"
      },
      {
        id: 2,
        statement: "Tôi có thể kiểm soát cảm xúc tiêu cực của mình",
        type: "self-regulation"
      }
      // ... More default questions for fallback
    ];
  };

  // Timer countdown (disabled for EQ test - no time limit)
  useEffect(() => {
    if (currentScreen === 'test' && timeLeft !== null && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && currentScreen === 'test') {
      calculateResult();
    }
  }, [timeLeft, currentScreen]);

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (showNeedAnswerNotification) {
      const timer = setTimeout(() => {
        setShowNeedAnswerNotification(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showNeedAnswerNotification]);

  const formatTime = (seconds) => {
    if (seconds === null) return 'Không giới hạn';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startTest = () => {
    setCurrentScreen('test');
    setCurrentQuestion(0);
    setAnswers([]);
    setTimeLeft(null); // No time limit for EQ test
    setIsReviewingMode(false); // Reset reviewing mode when starting test
    setReviewLaterQuestions([]); // Clear review later questions
    // Scroll to top when starting test
    window.scrollTo(0, 0);
  };

  const handleAnswer = (answerIndex) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = answerIndex;
    setAnswers(newAnswers);
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setShowNeedAnswerNotification(false); // Hide notification when moving to next question
      setIsReviewingMode(false); // Exit reviewing mode when navigating manually
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setShowNeedAnswerNotification(false); // Hide notification when moving to previous question
      setIsReviewingMode(false); // Exit reviewing mode when navigating manually
    }
  };

  const toggleReviewLater = () => {
    const newReviewLater = [...reviewLaterQuestions];
    const wasMarkedForReview = newReviewLater.includes(currentQuestion);
    
    if (wasMarkedForReview) {
      // Remove from review list (bỏ đánh dấu)
      newReviewLater.splice(newReviewLater.indexOf(currentQuestion), 1);
    } else {
      // Add to review list (đánh dấu)
      newReviewLater.push(currentQuestion);
    }
    setReviewLaterQuestions(newReviewLater);
    
    // Handle reviewing mode logic
    if (isReviewingMode && wasMarkedForReview) {
      // If we're in reviewing mode and user clicked "Bỏ đánh dấu"
      // Navigate to next purple question (same as when selecting answer)
      setIsAutoAdvancing(true);
      setTimeout(() => {
        if (newReviewLater.length > 0) {
          // Go to next purple question
          const nextReviewQuestion = findNextReviewQuestion(currentQuestion, newReviewLater);
          setCurrentQuestion(nextReviewQuestion);
        } else {
          // No more review questions, go to last question and exit review mode
          setCurrentQuestion(questions.length - 1);
          setIsReviewingMode(false);
        }
        
        setIsAutoAdvancing(false);
      }, 200);
    } else {
      // Normal mode or marking for review - show notification and stay at current question
      setShowNeedAnswerNotification(true);
      
      // Exit reviewing mode when user marks question for review
      setIsReviewingMode(false);
    }
  };

  const goToQuestion = (index) => {
    setCurrentQuestion(index);
    setShowNeedAnswerNotification(false); // Hide notification when jumping to specific question
    setIsReviewingMode(false); // Exit reviewing mode when jumping to specific question
  };

  // Helper function to find the next purple question (review later)
  const findNextReviewQuestion = (currentIndex, reviewList) => {
    // Find the next review question after currentIndex
    const nextReviewQuestions = reviewList.filter(q => q > currentIndex);
    if (nextReviewQuestions.length > 0) {
      return Math.min(...nextReviewQuestions);
    }
    
    // If no review questions after current, find the first one overall
    const allReviewQuestions = reviewList.filter(q => q !== currentIndex);
    if (allReviewQuestions.length > 0) {
      return Math.min(...allReviewQuestions);
    }
    
    // If no more review questions, return last question
    return questions.length - 1;
  };

  const finishTest = () => {
    const answeredCount = answers.filter(a => a !== undefined && a !== null).length;
    if (answeredCount < questions.length) {
      setShowIncompleteModal(true);
      return;
    }
    
    // Check if there are questions marked for review
    if (reviewLaterQuestions.length > 0) {
      setShowReviewModal(true);
      return;
    }
    
    calculateResult();
  };

  const handleForceSubmit = () => {
    setShowReviewModal(false);
    setIsReviewingMode(false); // Exit reviewing mode when force submit
    calculateResult();
  };

  const handleGoToReview = () => {
    setShowReviewModal(false);
    setIsReviewingMode(true); // Enable reviewing mode
    // Go to the first review question (smallest index)
    const firstReviewQuestion = Math.min(...reviewLaterQuestions);
    setCurrentQuestion(firstReviewQuestion);
  };

  const selectAnswer = (answerIndex) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = answerIndex;
    setAnswers(newAnswers);
    
    // Hide notification if it was showing
    setShowNeedAnswerNotification(false);
    
    // Handle reviewing mode logic
    if (isReviewingMode) {
      // Remove current question from review list (turn green)
      const newReviewLater = reviewLaterQuestions.filter(q => q !== currentQuestion);
      setReviewLaterQuestions(newReviewLater);
      
      setIsAutoAdvancing(true);
      setTimeout(() => {
        if (newReviewLater.length > 0) {
          // Go to next purple question
          const nextReviewQuestion = findNextReviewQuestion(currentQuestion, newReviewLater);
          setCurrentQuestion(nextReviewQuestion);
        } else {
          // No more review questions, go to last question and exit review mode
          setCurrentQuestion(questions.length - 1);
          setIsReviewingMode(false);
        }
        
        setIsAutoAdvancing(false);
      }, 200);
    } else {
      // Normal mode - Auto-advance logic - chỉ đi sequential, không đi đến review questions
      setIsAutoAdvancing(true);
      setTimeout(() => {
        if (currentQuestion < questions.length - 1) {
          // Go to next sequential question
          setCurrentQuestion(currentQuestion + 1);
        } else {
          // Go to last question for submit
          setCurrentQuestion(questions.length - 1);
        }
        
        setIsAutoAdvancing(false);
      }, 200);
    }
  };

  const calculateResult = () => {
    // Calculate EQ score based on 5 domains
    const domains = {
      self_awareness: [],
      self_regulation: [],
      motivation: [],
      empathy: [],
      social_skills: []
    };

    // Group scores by domain
    answers.forEach((answer, index) => {
      if (answer !== undefined && answer !== null) {
        const questionType = questions[index]?.type;
        if (domains[questionType]) {
          domains[questionType].push(answer);
        }
      }
    });

    // Calculate domain scores (average)
    const domainScores = {};
    let totalScore = 0;
    let domainCount = 0;

    Object.keys(domains).forEach(domain => {
      if (domains[domain].length > 0) {
        const domainSum = domains[domain].reduce((sum, score) => sum + score, 0);
        const domainAverage = domainSum / domains[domain].length;
        domainScores[domain] = Math.round(domainAverage * 20); // Convert to 100 scale
        totalScore += domainAverage;
        domainCount++;
      } else {
        domainScores[domain] = 0;
      }
    });

    const eqScore = domainCount > 0 ? Math.round((totalScore / domainCount) * 20) : 0;

    const detailsString = `Phân tích chi tiết EQ:
• Điểm tổng: ${eqScore}/100
• Tự nhận thức: ${domainScores.self_awareness}/100
• Tự điều chỉnh: ${domainScores.self_regulation}/100  
• Động lực: ${domainScores.motivation}/100
• Đồng cảm: ${domainScores.empathy}/100
• Kỹ năng xã hội: ${domainScores.social_skills}/100
• Mức độ: ${getEQLevel(eqScore).level}`;

    // Navigate to test result page with data
    navigate('/test-result', {
      state: {
        testType: 'eq',
        result: eqScore,
        details: getEQDescription(eqScore),
        breakdown: detailsString
      }
    });
  };

  const getEQDescription = (score) => {
    if (score >= 85) return 'Trí tuệ cảm xúc xuất sắc - Có khả năng quản lý cảm xúc và giao tiếp tuyệt vời';
    if (score >= 70) return 'Trí tuệ cảm xúc cao - Hiểu biết tốt về cảm xúc bản thân và người khác';
    if (score >= 55) return 'Trí tuệ cảm xúc trung bình khá - Có thể phát triển thêm các kỹ năng cảm xúc';
    if (score >= 40) return 'Trí tuệ cảm xúc trung bình - Cần rèn luyện thêm kỹ năng EQ';
    return 'Trí tuệ cảm xúc thấp - Nên tham gia các khóa học phát triển kỹ năng cảm xúc';
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (password === correctPassword) {
      setCurrentScreen('result');
      setPasswordError('');

      // Track password verification
      await trackPasswordVerification('eq');
    } else {
      setPasswordError('Mật khẩu không chính xác. Vui lòng thử lại!');
    }
  };

  const getEQLevel = (score) => {
    if (score >= 85) return { level: "Rất cao", color: "text-green-600", description: "Bạn có trí tuệ cảm xúc xuất sắc" };
    if (score >= 70) return { level: "Cao", color: "text-blue-600", description: "Bạn có trí tuệ cảm xúc tốt" };
    if (score >= 55) return { level: "Trung bình khá", color: "text-yellow-600", description: "Bạn có trí tuệ cảm xúc ở mức trung bình khá" };
    if (score >= 40) return { level: "Trung bình", color: "text-orange-600", description: "Bạn có trí tuệ cảm xúc ở mức trung bình" };
    return { level: "Thấp", color: "text-red-600", description: "Bạn cần cải thiện trí tuệ cảm xúc" };
  };

  const getQuestionStatus = (index) => {
    if (reviewLaterQuestions.includes(index)) {
      return answers[index] !== undefined && answers[index] !== null ? 'completed-review' : 'review';
    }
    if (answers[index] !== undefined && answers[index] !== null) return 'completed';
    return 'unanswered';
  };

  const getQuestionStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500 text-white';
      case 'answered': return 'bg-green-500 text-white'; // backward compatibility
      case 'review': return 'bg-purple-500 text-white';
      case 'completed-review': return 'bg-purple-500 text-white';
      default: return 'bg-white border-2 border-gray-300 text-gray-700';
    }
  };

  // Mini question board component
  const ModernQuestionBoard = () => (
    <div className="bg-white rounded-xl shadow-lg px-5 py-5 sticky top-8">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-gray-800 text-center">Bảng theo dõi</h3>
      </div>
      
      <div className="grid grid-cols-7 gap-2 mb-5">
        {questions.map((_, index) => (
          <button
            key={index}
            onClick={() => goToQuestion(index)}
            className={`w-8 h-8 rounded-full text-xs font-semibold transition-all duration-200 hover:scale-105 ${
              index === currentQuestion 
                ? 'ring-2 ring-blue-400 ring-offset-1 ' + getQuestionStatusColor(getQuestionStatus(index))
                : getQuestionStatusColor(getQuestionStatus(index))
            }`}
          >
            {index + 1}
          </button>
        ))}
      </div>
      
      <button
        onClick={finishTest}
        className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-2.5 px-3 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg text-sm mb-5"
      >
        NỘP BÀI
      </button>
      
      <div className="grid grid-cols-3 gap-1 text-xs">
        <div className="flex flex-col items-center">
          <div className="w-2.5 h-2.5 bg-green-500 rounded-full mb-1"></div>
          <span className="text-gray-600">Đã làm</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-2.5 h-2.5 bg-gray-300 rounded-full mb-1"></div>
          <span className="text-gray-600">Chưa làm</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-2.5 h-2.5 bg-purple-500 rounded-full mb-1"></div>
          <span className="text-gray-600">Cần kiểm tra</span>
        </div>
      </div>
    </div>
  );

  // Intro Screen
  if (currentScreen === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 relative">
        {/* Subtle background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 bg-blue-100 rounded-full opacity-30 blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-purple-100 rounded-full opacity-20 blur-3xl"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto text-center">
            {/* Header */}
            <div className="mb-8">
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-medium rounded-full mb-4">
                ✨ Bài test EQ chính thức
              </span>
              
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Test <span className="text-purple-600">EQ</span> Online - Kiểm tra trí tuệ cảm xúc
              </h1>
              
              <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
                Đánh giá chỉ số cảm xúc và trí tuệ cảm xúc của bạn với bài test được thiết kế theo tiêu chuẩn quốc tế
              </p>
            </div>

            {/* Main card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-100">
              <h2 className="text-2xl font-semibold text-gray-800 mb-8">Thông tin bài Test EQ chuẩn Quốc Tế</h2>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">{questions.length}</div>
                  <div className="text-blue-700 font-medium">Câu hỏi</div>
                </div>
                
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">∞</div>
                  <div className="text-purple-700 font-medium">Không giới hạn thời gian</div>
                </div>
                
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">5</div>
                  <div className="text-green-700 font-medium">Nhóm kỹ năng EQ</div>
                </div>
              </div>
              
              {/* CTA Button */}
              <button 
                onClick={startTest}
                className="group relative bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold text-lg py-4 px-12 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 mx-auto"
              >
                <span className="flex items-center justify-center">
                  Bắt đầu làm bài ngay
                </span>
              </button>
              
              <p className="text-sm text-gray-500 mt-4">
                Miễn phí • Không cần đăng ký • Kết quả tức thì
              </p>
            </div>

            {/* Guidelines */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-left">
              <div className="flex items-center mb-8">
                <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center mr-4">
                  <span className="text-2xl">💡</span>
                </div>
                <h3 className="text-2xl font-semibold text-gray-800">Lưu ý quan trọng</h3>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <h4 className="font-semibold text-blue-700 mb-3">Trả lời chân thành</h4>
                  <p className="text-gray-600 text-sm">
                    Chọn đáp án phản ánh đúng cách bạn cảm nhận và hành xử thực tế
                  </p>
                </div>
                
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <h4 className="font-semibold text-green-700 mb-3">Không có đáp án sai</h4>
                  <p className="text-gray-600 text-sm">
                    Mỗi câu trả lời chỉ thể hiện mức độ phù hợp với tính cách của bạn
                  </p>
                </div>
                
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <h4 className="font-semibold text-purple-700 mb-3">Thoải mái làm bài</h4>
                  <p className="text-gray-600 text-sm">
                    Không giới hạn thời gian, bạn có thể suy nghĩ kỹ cho từng câu
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Test Screen
  if (currentScreen === 'test') {
    // Don't render if questions not loaded yet
    if (isLoading || questions.length === 0) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-lg text-gray-600">🔄 Đang tải câu hỏi EQ từ database...</p>
            <p className="mt-2 text-sm text-gray-500">Vui lòng chờ trong giây lát</p>
          </div>
        </div>
      );
    }

    const answeredCount = answers.filter(a => a !== undefined && a !== null).length;
    const progress = (answeredCount / questions.length) * 100;
    
    return (
              <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-3">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="xl:col-span-2">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-lg p-4 md:p-5 mb-4">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-3">
                      <h2 className="text-xl md:text-2xl font-bold text-gray-800">{seoData.h1 || "Test EQ chuẩn Emotional Intelligence"}</h2>
                      {isReviewingMode && (
                        <div className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium animate-pulse">
                          🔍 Đang kiểm tra lại
                        </div>
                      )}
                    </div>
                    <div className="bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-base font-bold">
                      ⏱️ {formatTime(timeLeft)}
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Tiến độ: {answeredCount} / {questions.length}</span>
                    <span>Câu hiện tại: {currentQuestion + 1}</span>
                  </div>
                </div>

                {/* Mobile Progress Grid */}
                <div className="bg-white rounded-lg shadow-lg p-3 mb-4 xl:hidden">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-gray-800 text-sm">Bảng theo dõi</h3>
                    <button
                      onClick={finishTest}
                      className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-2 px-4 rounded-lg text-sm"
                    >
                      NỘP BÀI
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-10 gap-1 mb-3">
                    {questions.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => goToQuestion(index)}
                        className={`w-7 h-7 rounded-full text-xs font-medium transition-all duration-200 ${
                          index === currentQuestion 
                            ? 'ring-2 ring-blue-400 ' + getQuestionStatusColor(getQuestionStatus(index))
                            : getQuestionStatusColor(getQuestionStatus(index))
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-600">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded mr-1"></div>
                      <span>Đã làm</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-white border border-gray-300 rounded mr-1"></div>
                      <span>Chưa làm</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-purple-500 rounded mr-1"></div>
                      <span>Cần kiểm tra</span>
                    </div>
                  </div>
                </div>

                {/* Question */}
                <div className="bg-white rounded-lg shadow-lg p-5 mb-4">
                  <div className="flex items-center mb-4">
                    <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-semibold mr-4">
                      {currentQuestion + 1}
                    </span>
                    <h3 className="text-xl font-semibold text-gray-800">
                      {(() => {
                        const currentQ = questions[currentQuestion];
                        console.log('EQ Current Question:', currentQ);
                        console.log('EQ Current Question Index:', currentQuestion);
                        console.log('EQ Questions Array:', questions);
                        return currentQ?.statement || currentQ?.question || 'Không có câu hỏi';
                      })()}
                    </h3>
                  </div>
                  
                                      <div className="space-y-4">
                      {/* Likert Scale (1-5) */}
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          onClick={() => {
                            selectAnswer(rating);
                            handleAnswer(rating);
                          }}
                          className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                            answers[currentQuestion] === rating
                              ? 'border-blue-500 bg-blue-50 text-blue-800 shadow-sm'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center">
                            <div className={`w-8 h-8 rounded-full border-2 mr-4 flex items-center justify-center ${
                              answers[currentQuestion] === rating
                                ? 'border-blue-500 bg-blue-500'
                                : 'border-gray-300'
                            }`}>
                              <span className={`text-base font-bold ${
                                answers[currentQuestion] === rating
                                  ? 'text-white'
                                  : 'text-gray-600'
                              }`}>
                                {rating}
                              </span>
                            </div>
                            <span className="text-gray-800 text-lg">
                              {rating === 1 && 'Hoàn toàn không đồng ý'}
                              {rating === 2 && 'Không đồng ý'}
                              {rating === 3 && 'Trung tính'}
                              {rating === 4 && 'Đồng ý'}
                              {rating === 5 && 'Hoàn toàn đồng ý'}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  
                  {/* Review Later Button Area - Mobile Responsive */}
                  <div className="mt-8 space-y-4">
                    {/* Review Later Button */}
                    <div className="flex justify-center">
                      <button
                        onClick={toggleReviewLater}
                        className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg font-medium transition-all duration-200 text-xs sm:text-sm ${
                          reviewLaterQuestions.includes(currentQuestion)
                            ? 'bg-purple-500 text-white hover:bg-purple-600'
                            : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                        }`}
                      >
                        {reviewLaterQuestions.includes(currentQuestion) ? 'Bỏ đánh dấu' : 'Lát kiểm tra lại'}
                      </button>
                    </div>
                    
                    {/* Answer Required Notification - Mobile Responsive */}
                    {showNeedAnswerNotification && (
                      <div className="flex justify-center px-4">
                        <div className="flex items-center space-x-2 bg-blue-600 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow-md border border-blue-700 text-center max-w-full">
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-xs sm:text-sm font-medium break-words">Vui lòng chọn trước một đáp án để tiếp tục!</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Navigation */}
                <div className="bg-white rounded-lg shadow-lg p-4 sticky bottom-4 z-10">
                  <div className="flex justify-between items-center">
                    <button
                      onClick={prevQuestion}
                      disabled={currentQuestion === 0}
                      className="flex items-center px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm"
                    >
                      ← Câu trước
                    </button>

                    <div className="flex gap-3">
                      {isAutoAdvancing ? (
                        <div className="px-4 py-2 bg-blue-100 text-blue-600 rounded-lg text-sm font-medium flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Đang chuyển...
                        </div>
                      ) : currentQuestion === questions.length - 1 ? (
                        <button
                          onClick={finishTest}
                          className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 font-medium transition-all duration-200 text-sm"
                        >
                          Hoàn thành
                        </button>
                      ) : (
                        <button
                          onClick={nextQuestion}
                          className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:from-blue-600 hover:to-indigo-700 font-medium transition-all duration-200 text-sm"
                        >
                          Câu tiếp →
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Modern Question Board - Desktop Only */}
              <div className="hidden xl:block">
                <ModernQuestionBoard />
              </div>
            </div>
          </div>
        </div>

        {/* Incomplete Modal */}
        {showIncompleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 mx-4 max-w-md w-full">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 mb-4">
                  <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Chưa hoàn thành đủ câu hỏi
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Bạn cần hoàn thành tất cả {questions.length} câu hỏi trước khi nộp bài! 
                  Hiện tại bạn đã làm {answers.filter(a => a !== undefined && a !== null).length}/{questions.length} câu.
                </p>
                <button
                  type="button"
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-md font-medium hover:bg-blue-700 transition-colors"
                  onClick={() => setShowIncompleteModal(false)}
                >
                  Tiếp tục làm bài
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Review Later Modal */}
        {showReviewModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 mx-4 max-w-md w-full">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 mb-4">
                  <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Còn câu hỏi cần kiểm tra lại
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Bạn có {reviewLaterQuestions.length} câu hỏi được đánh dấu cần kiểm tra lại. 
                  Bạn có muốn xem lại những câu này trước khi nộp bài không?
                </p>
                <div className="space-y-3">
                  <button
                    type="button"
                    className="w-full bg-purple-600 text-white px-4 py-2 rounded-md font-medium hover:bg-purple-700 transition-colors"
                    onClick={handleGoToReview}
                  >
                    Xem lại câu hỏi
                  </button>
                  <button
                    type="button"
                    className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md font-medium hover:bg-gray-300 transition-colors"
                    onClick={handleForceSubmit}
                  >
                    Nộp bài ngay
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}


      </div>
    );
  }

  // Completed Screen
  if (currentScreen === 'completed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 py-3">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="mb-8">
              <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-800 mb-4">
                🎉 Chúc mừng! Bạn đã hoàn thành bài test!
              </h1>
              <p className="text-lg text-gray-600">
                Nhập mật khẩu để xem kết quả chi tiết
              </p>
            </div>

            <form onSubmit={handlePasswordSubmit} className="mb-8">
              <div className="relative mb-4">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none"
                  placeholder="Nhập mật khẩu để xem kết quả"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-500"
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {passwordError && <p className="text-red-500 text-sm mb-4">{passwordError}</p>}
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold py-3 px-6 rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200"
              >
                Xem kết quả
              </button>
            </form>

            <button
              onClick={() => navigate('/')}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              ← Quay về trang chủ
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Result Screen
  if (currentScreen === 'result') {
    const eqLevel = getEQLevel(eqScore);
    const completedAnswers = answers.filter(a => a !== undefined && a !== null).length;
    const completionRate = ((completedAnswers / questions.length) * 100).toFixed(1);

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-3">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-gray-800 mb-4">
                🧠 Kết quả Test EQ
              </h1>
              <p className="text-lg text-gray-600">
                Dưới đây là kết quả chi tiết về trí tuệ cảm xúc của bạn
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl p-6 text-white text-center">
                <h3 className="text-xl font-semibold mb-2">Chỉ số EQ của bạn</h3>
                <div className="text-5xl font-bold mb-2">{eqScore}</div>
                <p className={`text-lg font-medium ${eqLevel.color.replace('text-', 'text-white')}`}>
                  {eqLevel.level}
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Số câu đã trả lời:</span>
                    <span className="font-bold text-gray-800">{completedAnswers}/{questions.length}</span>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Tỷ lệ hoàn thành:</span>
                    <span className="font-bold text-gray-800">{completionRate}%</span>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Thời gian làm bài:</span>
                    <span className="font-bold text-gray-800">{formatTime(timeLeft)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Đánh giá chung</h3>
              <p className="text-gray-700 mb-4">{eqLevel.description}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Điểm mạnh:</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {eqScore >= 100 && <li>• Khả năng nhận thức cảm xúc tốt</li>}
                    {eqScore >= 90 && <li>• Biết cách quản lý stress</li>}
                    {eqScore >= 80 && <li>• Có khả năng đồng cảm</li>}
                    {eqScore >= 70 && <li>• Giao tiếp khá hiệu quả</li>}
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Cần cải thiện:</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {eqScore < 100 && <li>• Tăng cường kỹ năng lắng nghe</li>}
                    {eqScore < 90 && <li>• Cải thiện khả năng kiểm soát cảm xúc</li>}
                    {eqScore < 80 && <li>• Phát triển kỹ năng đồng cảm</li>}
                    {eqScore < 70 && <li>• Nâng cao kỹ năng giao tiếp</li>}
                  </ul>
                </div>
              </div>
            </div>

            <div className="text-center">
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/test-eq')}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200"
                >
                  Làm lại bài test
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200"
                >
                  Quay về trang chủ
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default TestEQ; 